import { useCallback, useEffect, useState } from 'react';
import { FlatList, Image, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import {
  adminCreditCoins,
  approveTopup,
  getAdminTopupProofToken,
  listPendingTopups,
  rejectTopup,
  PendingTopup,
} from '../api/admin';
import { Button, Card, ConfirmDialog, EmptyState, ErrorText, FormInput, Label, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';
import { adminTopupProofUrl } from '../config';

/** Fetches its own short-lived photo token — a session-wide token can't be
 * embedded directly in the image URL (see backend JwtPhotoStrategy). */
function AdminTopupProofPhoto({ token, transactionId }: { token: string; transactionId: string }) {
  const [photoToken, setPhotoToken] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getAdminTopupProofToken(token, transactionId)
      .then((r) => {
        if (!cancelled) setPhotoToken(r.token);
      })
      .catch(() => {
        if (!cancelled) setPhotoToken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, transactionId]);
  if (!photoToken) return null;
  return (
    <Image
      source={{ uri: adminTopupProofUrl(transactionId, photoToken) }}
      style={{ width: '100%', height: 180, borderRadius: 8, marginTop: 10 }}
      resizeMode="contain"
    />
  );
}

export default function AdminTopupsScreen() {
  const { token } = useAuth();
  const [requests, setRequests] = useState<PendingTopup[]>([]);
  const [loading, setLoading] = useState(true);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [identifier, setIdentifier] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [creditBusy, setCreditBusy] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [confirmCreditVisible, setConfirmCreditVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ id: string; action: 'approve' | 'reject' } | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setRequests(await listPendingTopups(token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không tải được danh sách yêu cầu nạp tiền, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onApprove = (id: string) => setPendingAction({ id, action: 'approve' });
  const onReject = (id: string) => setPendingAction({ id, action: 'reject' });

  const confirmPendingAction = async () => {
    if (!token || !pendingAction) return;
    const { id, action } = pendingAction;
    setBusyId(id);
    setError(null);
    try {
      if (action === 'approve') {
        await approveTopup(token, id);
      } else {
        await rejectTopup(token, id, reasons[id]);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : action === 'approve' ? 'Duyệt thất bại' : 'Từ chối thất bại');
    } finally {
      setBusyId(null);
      setPendingAction(null);
    }
  };

  const pendingRequest = pendingAction ? requests.find((r) => r.id === pendingAction.id) : null;

  const onCredit = () => {
    setCreditError(null);
    setCreditMessage(null);
    if (!identifier.trim()) {
      setCreditError('Vui lòng nhập email hoặc mã giới thiệu');
      return;
    }
    const amount = Number(creditAmount);
    if (!Number.isInteger(amount) || amount < 1) {
      setCreditError('Số xu phải là số nguyên dương');
      return;
    }
    setConfirmCreditVisible(true);
  };

  const confirmCredit = async () => {
    if (!token) return;
    const amount = Number(creditAmount);
    setCreditBusy(true);
    try {
      const result = await adminCreditCoins(token, identifier.trim(), amount, creditNote.trim() || undefined);
      setCreditMessage(
        `Đã cộng ${amount} xu cho ${result.creditedUser.name} (${result.creditedUser.email}) — kiểm tra kỹ đúng người trước khi cộng lần sau`,
      );
      setIdentifier('');
      setCreditAmount('');
      setCreditNote('');
    } catch (e) {
      setCreditError(e instanceof ApiError ? e.message : 'Cộng xu thất bại');
    } finally {
      setCreditBusy(false);
      setConfirmCreditVisible(false);
    }
  };

  return (
    <ScreenContainer>
      <Card>
        <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>
          Nạp xu trực tiếp
        </Text>
        <Label>Email hoặc mã giới thiệu</Label>
        <FormInput
          value={identifier}
          onChangeText={setIdentifier}
          placeholder="email@vidu.com hoặc JY3KF88"
          autoCapitalize="none"
        />
        <Label>Số xu</Label>
        <FormInput value={creditAmount} onChangeText={setCreditAmount} keyboardType="numeric" placeholder="VD: 5000" />
        <Label>Ghi chú (tuỳ chọn)</Label>
        <FormInput value={creditNote} onChangeText={setCreditNote} placeholder="VD: quà tặng test" />
        <ErrorText>{creditError}</ErrorText>
        {creditMessage ? <Text style={{ color: colors.success, marginBottom: 10 }}>{creditMessage}</Text> : null}
        <Button title="Cộng xu" onPress={onCredit} loading={creditBusy} />
      </Card>

      <ErrorText>{error}</ErrorText>
      <FlatList
        data={requests}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={!loading && !error ? <EmptyState text="Không có yêu cầu nạp tiền nào đang chờ" /> : null}
        renderItem={({ item }) => (
          <Card>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text }}>
              {item.user?.name ?? item.userId}
            </Text>
            <Text style={{ color: colors.textMuted, marginTop: 2 }}>{item.user?.email}</Text>
            <Text style={{ color: colors.text, marginTop: 8, fontSize: 15, fontFamily: fonts.semiBold }}>
              Nạp {(item.requestedAmountVnd ?? 0).toLocaleString('vi-VN')}đ (≈ {item.amountCoins} xu)
            </Text>
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>{item.note}</Text>
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 11 }}>
              {new Date(item.createdAt).toLocaleString('vi-VN')}
            </Text>
            {item.proofPhotoUrl && token ? (
              <AdminTopupProofPhoto token={token} transactionId={item.id} />
            ) : (
              <Text style={{ color: colors.textMuted, marginTop: 8, fontSize: 12, fontStyle: 'italic' }}>
                Chưa có ảnh chuyển khoản
              </Text>
            )}

            <View style={{ marginTop: 12 }}>
              <Label>Lý do từ chối (nếu từ chối)</Label>
              <FormInput
                value={reasons[item.id] ?? ''}
                onChangeText={(v) => setReasons((r) => ({ ...r, [item.id]: v }))}
                placeholder="Ví dụ: không thấy giao dịch chuyển khoản tương ứng"
              />
              <Button
                title="Đã nhận tiền — Duyệt"
                onPress={() => onApprove(item.id)}
                loading={busyId === item.id}
              />
              <Button
                title="Từ chối"
                variant="danger"
                onPress={() => onReject(item.id)}
                loading={busyId === item.id}
              />
            </View>
          </Card>
        )}
      />
      <ConfirmDialog
        visible={confirmCreditVisible}
        title="Xác nhận cộng xu"
        message={`Cộng ${creditAmount} xu cho "${identifier.trim()}"${creditNote.trim() ? ` (${creditNote.trim()})` : ''}? Hãy chắc chắn đúng người trước khi xác nhận.`}
        confirmLabel="Cộng xu"
        loading={creditBusy}
        onConfirm={confirmCredit}
        onCancel={() => setConfirmCreditVisible(false)}
      />
      <ConfirmDialog
        visible={pendingAction !== null}
        title={pendingAction?.action === 'approve' ? 'Xác nhận duyệt nạp tiền' : 'Xác nhận từ chối nạp tiền'}
        message={
          pendingRequest
            ? pendingAction?.action === 'approve'
              ? `Xác nhận đã nhận được ${(pendingRequest.requestedAmountVnd ?? 0).toLocaleString('vi-VN')}đ từ "${pendingRequest.user?.name ?? pendingRequest.userId}" và cộng ${pendingRequest.amountCoins} xu?`
              : `Từ chối yêu cầu nạp tiền của "${pendingRequest.user?.name ?? pendingRequest.userId}"?`
            : ''
        }
        confirmLabel={pendingAction?.action === 'approve' ? 'Duyệt' : 'Từ chối'}
        danger={pendingAction?.action === 'reject'}
        loading={busyId !== null}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />
    </ScreenContainer>
  );
}
